import React from 'react';
import { motion } from 'framer-motion';
import { Layers, Shuffle, Lock, EyeOff } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useThemeMode } from '../../hooks/useThemeMode';

const features = [
  {
    icon: Layers,
    title: "Multi-Chain Bridge",
    desc: "Private bridge between Ethereum, Solana, BNB Chain, Polygon, Base, Hyperliquid with complete anonymity."
  },
  {
    icon: Shuffle,
    title: "Dual Key System",
    desc: "Proprietary Ed25519 (Solana) and ECDSA (EVM) key generation allows seamless cross-ecosystem privacy."
  },
  {
    icon: EyeOff,
    title: "Complete Unlinkability",
    desc: "Mathematical guarantee: no connection between source and destination addresses across any chain."
  },
  {
    icon: Lock,
    title: "Privacy First",
    desc: "Not just a mixer. A complete privacy protocol. Bridging is just one feature of the InterNull architecture."
  }
];

const Features = () => {
  const navigate = useNavigate();
  const { isDark } = useThemeMode();

  return (
    <section id="features" className="py-24 px-6 max-w-7xl mx-auto">
      <div className="grid lg:grid-cols-2 gap-16">
        <div>
          <motion.h2
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className={`text-4xl md:text-5xl font-bold leading-tight mb-8 ${isDark ? 'text-white' : 'text-neutral-900'}`}
          >
            Mathematical <br />
            <span style={{ color: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.4)' }}>Guarantee.</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className={`text-lg leading-relaxed mb-8 ${isDark ? 'text-neutral-400' : 'text-neutral-500'}`}
          >
            InterNull utilizes advanced cryptographic primitives to sever the link between deposit and withdrawal. By pooling funds and utilizing One-Time-Signature keys, we ensure that your financial history remains yours alone.
          </motion.p>

          <motion.button
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.4 }}
            onClick={() => navigate('/technical-paper')}
            className={`border-b pb-1 transition-colors ${isDark ? 'border-white text-white hover:text-neutral-300' : 'border-neutral-900 text-neutral-900 hover:text-neutral-600'}`}
            style={{ backgroundColor: 'transparent' }}
          >
            Read the Whitepaper
          </motion.button>
        </div>

        <div className="grid sm:grid-cols-2 gap-6">
          {features.map((feature, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 + (index * 0.1) }}
              className="p-6 transition-all duration-300"
              style={{
                backgroundColor: isDark ? 'rgba(23,23,23,0.3)' : 'rgba(0,0,0,0.02)',
                borderLeft: `2px solid ${isDark ? '#404040' : '#d4d4d4'}`,
              }}
            >
              <feature.icon className={isDark ? 'text-white mb-4' : 'text-neutral-900 mb-4'} size={28} />
              <h3 className={`text-lg font-bold mb-2 ${isDark ? 'text-white' : 'text-neutral-900'}`}>{feature.title}</h3>
              <p className={`text-sm leading-relaxed ${isDark ? 'text-neutral-400' : 'text-neutral-500'}`}>
                {feature.desc}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Features;
